import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Pause, CalendarClock, LayoutList, Ban, X, Search, Route } from "lucide-react";
import { DlrTracesTab } from "./DlrTraces.js";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { DataTable, type Column } from "@/components/ui/DataTable.js";
import { StatusBadge } from "@/components/ui/StatusBadge.js";
import { api } from "@/lib/api.js";
import { relativeTime, formatPercent } from "@/lib/format.js";
import { useAuthStore } from "@/lib/auth.js";
import type {
  HsmTemplate,
  ScheduledCampaign,
  CancelScheduledCampaignResult,
} from "@bo/shared";

const STATUS_LABEL: Record<string, string> = {
  PENDING: "Pendiente",
  SENT: "Enviada",
  CANCELLED: "Cancelada",
  SENT_CANCELLED: "Enviada (canc. tardía)",
  UNKNOWN: "—",
};

function fmtScheduled(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-GT", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

// ---------------------------------------------------------------------------
// Modal de cancelación de campaña programada
// ---------------------------------------------------------------------------
function CancelCampaignModal({
  campaign,
  onClose,
  onDone,
}: {
  campaign: ScheduledCampaign;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const cancel = useMutation({
    mutationFn: () =>
      api.post<CancelScheduledCampaignResult>(
        `/templates/scheduled/${campaign.idplantilla}/cancel`,
        { reason },
      ),
    onSuccess: (res) => {
      if (res.outcome === "MARKED_IN_FLIGHT") {
        toast.warning(res.message, { duration: 12000 });
      } else {
        toast.success(res.message);
      }
      onDone();
      onClose();
    },
    onError: (err: Error) => toast.error(err.message, { duration: 8000 }),
  });

  return (
    <div className="fixed inset-0 bg-slate-950/40 z-50 grid place-items-center p-4">
      <div className="card w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-lg">Cancelar envío programado</h2>
            <p className="text-xs text-slate-500 mt-1">
              La campaña no se enviará. Queda registrado en el audit y en la
              bitácora de la plantilla.
            </p>
          </div>
          <button className="btn-ghost p-1" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 text-sm space-y-1">
          <p className="font-medium">{campaign.nombre ?? campaign.templateName ?? "—"}</p>
          <p className="text-xs text-slate-500">
            <span className="mono">#{campaign.idplantilla}</span> ·{" "}
            {campaign.companyName ?? campaign.idcompany ?? "—"}
          </p>
          <p className="text-xs text-slate-500">
            Programada para:{" "}
            <span className="font-medium">{fmtScheduled(campaign.scheduledAt)}</span>
          </p>
          <p className="text-xs text-slate-500">
            Destinatarios: <span className="font-medium">{campaign.recipients}</span>
          </p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Motivo de la cancelación *</label>
          <textarea
            className="input min-h-[80px]"
            placeholder="Ej: el cliente solicitó cancelar este envío…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
          />
        </div>

        <div className="flex gap-2">
          <button className="btn-secondary flex-1" onClick={onClose}>
            Volver
          </button>
          <button
            className="btn-danger flex-1"
            disabled={reason.trim().length < 3 || cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {cancel.isPending ? "Cancelando…" : "Cancelar envío"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Campañas programadas (core SM_PLANTILLA)
// ---------------------------------------------------------------------------
function ScheduledCampaignsTab({
  onVerTrazas,
}: {
  onVerTrazas: (id: number) => void;
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [page, setPage] = useState(1);
  const [cancelling, setCancelling] = useState<ScheduledCampaign | null>(null);
  const pageSize = 25;
  const canCancel = useAuthStore((s) =>
    s.hasPermission("template.cancel_scheduled"),
  );
  const canDlr = useAuthStore((s) => s.hasPermission("dlr.read"));

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (q) p.set("q", q);
    if (estado) p.set("estado", estado);
    return p.toString();
  }, [q, estado, page]);

  const { data, isLoading } = useQuery({
    queryKey: ["scheduled-campaigns", params],
    queryFn: () =>
      api.get<{ items: ScheduledCampaign[]; total: number }>(
        `/templates/scheduled?${params}`,
      ),
    refetchInterval: 30_000,
  });

  const cols: Column<ScheduledCampaign>[] = [
    {
      header: "ID",
      width: "90px",
      render: (r) => <span className="mono text-xs">{r.idplantilla}</span>,
    },
    {
      header: "Campaña",
      render: (r) => (
        <div>
          <div className="font-medium">{r.nombre ?? "—"}</div>
          {r.templateName && r.templateName !== r.nombre && (
            <div className="mono text-xs text-slate-500">{r.templateName}</div>
          )}
        </div>
      ),
    },
    {
      header: "Company",
      render: (r) => (
        <div>
          <div>{r.companyName ?? "—"}</div>
          <div className="text-xs mono text-slate-500">{r.idcompany}</div>
        </div>
      ),
    },
    {
      header: "Programada para",
      render: (r) => (
        <div className="text-xs">
          <div className="font-medium">{fmtScheduled(r.scheduledAt)}</div>
          <div className="text-slate-500">{relativeTime(r.scheduledAt)}</div>
        </div>
      ),
    },
    {
      header: "Destinatarios",
      width: "110px",
      render: (r) => r.recipients,
    },
    { header: "Programó", render: (r) => r.authorName ?? "—" },
    {
      header: "Estado",
      width: "110px",
      render: (r) => (
        <span
          className={
            r.status === "PENDING"
              ? "badge-warn"
              : r.status === "SENT"
                ? "badge-info"
                : r.status === "SENT_CANCELLED"
                  ? "badge-danger"
                  : "badge-neutral"
          }
        >
          {STATUS_LABEL[r.status] ?? r.status}
        </span>
      ),
    },
    {
      header: "",
      width: "210px",
      render: (r) => (
        <div className="flex gap-1 justify-end">
          {canDlr && (
            <button
              className="btn-ghost text-xs"
              onClick={() => onVerTrazas(r.idplantilla)}
              title="Ver trazas de entrega (DLR)"
            >
              <Route size={12} className="mr-1" />
              Trazas
            </button>
          )}
          {canCancel && r.status === "PENDING" && (
            <button
              className="btn-danger"
              onClick={() => setCancelling(r)}
              title="Cancelar este envío programado"
            >
              <Ban size={12} className="mr-1" />
              Cancelar
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            className="input pl-9"
            placeholder="Buscar por nombre o ID de plantilla…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <select
          className="input max-w-[180px]"
          value={estado}
          onChange={(e) => {
            setEstado(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          <option value="PENDING">Pendientes</option>
          <option value="SENT">Enviadas</option>
          <option value="CANCELLED">Canceladas</option>
          <option value="SENT_CANCELLED">Enviadas (canc. tardía)</option>
        </select>
        <span className="ml-auto text-xs text-slate-500 self-center">
          {data?.total ?? 0} campañas · auto-refresh 30s
        </span>
      </div>

      <DataTable
        columns={cols}
        rows={data?.items}
        loading={isLoading}
        getKey={(r) => r.idplantilla}
        empty="No hay campañas programadas."
      />

      {data && data.total > pageSize && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data.total)} de{" "}
            {data.total}
          </span>
          <div className="flex gap-2">
            <button
              className="btn-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </button>
            <button
              className="btn-secondary"
              disabled={page * pageSize >= data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400">
        Cancelar una campaña <span className="font-medium">Pendiente</span> evita el envío por
        completo (la lista de destinatarios se conserva como evidencia). Si el sistema ya la
        tomó justo en su hora programada, se marca cancelada y se reporta cuántos mensajes
        alcanzaron a salir — los que ya están en proceso no se pueden frenar desde aquí.
      </p>

      {cancelling && (
        <CancelCampaignModal
          campaign={cancelling}
          onClose={() => setCancelling(null)}
          onDone={() =>
            qc.invalidateQueries({ queryKey: ["scheduled-campaigns"] })
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Monitor HSM (existente)
// ---------------------------------------------------------------------------
function HsmMonitorTab() {
  const qc = useQueryClient();
  const canSync = useAuthStore((s) => s.hasPermission("template.sync"));
  const canPause = useAuthStore((s) => s.hasPermission("template.pause"));

  const { data, isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<{ items: HsmTemplate[] }>("/templates?pageSize=100"),
  });

  const sync = useMutation({
    mutationFn: (id: number) => api.post(`/templates/${id}/sync`),
    onSuccess: () => toast.success("Sincronización en cola"),
  });
  const pause = useMutation({
    mutationFn: (id: number) => api.post(`/templates/${id}/pause`),
    onSuccess: () => {
      toast.success("Plantilla pausada");
      qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const cols: Column<HsmTemplate>[] = [
    {
      header: "Plantilla",
      render: (r) => (
        <div>
          <div className="font-medium">{r.templateName}</div>
          <div className="mono text-xs text-slate-500">{r.templateExternalId}</div>
        </div>
      ),
    },
    { header: "Proveedor", render: (r) => <span className="badge-neutral">{r.providerCode}</span> },
    { header: "Idioma", render: (r) => r.language, width: "80px" },
    { header: "Categoría", render: (r) => r.categoryCode },
    {
      header: "Estado Meta",
      render: (r) => <StatusBadge value={r.metaStatusCode} />,
    },
    {
      header: "Último envío",
      render: (r) => (
        <div className="text-xs">
          <div>{relativeTime(r.lastSentAt)}</div>
          <div className="text-slate-500">
            {r.lastDelivered}/{r.lastTotal}
          </div>
        </div>
      ),
    },
    {
      header: "% Fallos",
      render: (r) => (
        <span className={r.lastFailureRate > 10 ? "text-rose-600 font-medium" : ""}>
          {formatPercent(r.lastFailureRate)}
        </span>
      ),
      width: "100px",
    },
    {
      header: "",
      width: "180px",
      render: (r) => (
        <div className="flex gap-2">
          {canSync && (
            <button
              className="btn-secondary"
              onClick={() => sync.mutate(r.id)}
              disabled={sync.isPending}
              title="Sincronizar con Meta"
            >
              <RefreshCw size={12} />
            </button>
          )}
          {canPause && r.metaStatusCode !== "PAUSED" && (
            <button
              className="btn-secondary"
              onClick={() => pause.mutate(r.id)}
              disabled={pause.isPending}
              title="Pausar plantilla"
            >
              <Pause size={12} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={cols}
      rows={data?.items}
      loading={isLoading}
      getKey={(r) => r.id}
      empty="No hay plantillas sincronizadas todavía."
    />
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------
export function TemplatesPage() {
  const [tab, setTab] = useState<"scheduled" | "hsm" | "dlr">("scheduled");
  const [dlrCampaign, setDlrCampaign] = useState<number | null>(null);
  const canDlr = useAuthStore((s) => s.hasPermission("dlr.read"));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Plantillas HSM"
        description="Campañas programadas y monitor de plantillas registradas en Meta."
      />

      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
        <button
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "scheduled"
              ? "border-blue-600 text-blue-700 dark:text-blue-400"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          onClick={() => setTab("scheduled")}
        >
          <CalendarClock size={15} />
          Campañas programadas
        </button>
        <button
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "hsm"
              ? "border-blue-600 text-blue-700 dark:text-blue-400"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          onClick={() => setTab("hsm")}
        >
          <LayoutList size={15} />
          Monitor HSM
        </button>
        {canDlr && (
          <button
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === "dlr"
                ? "border-blue-600 text-blue-700 dark:text-blue-400"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setTab("dlr")}
          >
            <Route size={15} />
            Trazas de entrega
          </button>
        )}
      </div>

      {tab === "scheduled" && (
        <ScheduledCampaignsTab
          onVerTrazas={(id) => {
            setDlrCampaign(id);
            setTab("dlr");
          }}
        />
      )}
      {tab === "hsm" && <HsmMonitorTab />}
      {tab === "dlr" && (
        <DlrTracesTab campaignId={dlrCampaign} onPickCampaign={setDlrCampaign} />
      )}
    </div>
  );
}
